import { useState, useMemo } from 'react';
import { Calculator, TrendingUp, Calendar, Coins } from 'lucide-react';
import { Card, Button } from '../ui';

interface YieldCalculatorProps {
  tokenPrice: number;
  annualYield: number;
  frequency: 'MONTHLY' | 'QUARTERLY' | 'SEMI_ANNUAL' | 'ANNUAL' | 'AT_MATURITY';
  maturityDate?: string;
  minInvestment?: number;
  maxInvestment?: number;
}

const frequencyLabels: Record<string, string> = {
  MONTHLY: 'Mensal',
  QUARTERLY: 'Trimestral',
  SEMI_ANNUAL: 'Semestral',
  ANNUAL: 'Anual',
  AT_MATURITY: 'No Vencimento'
};

const frequencyMultipliers: Record<string, number> = {
  MONTHLY: 12,
  QUARTERLY: 4,
  SEMI_ANNUAL: 2,
  ANNUAL: 1,
  AT_MATURITY: 1
};

export function YieldCalculator({
  tokenPrice,
  annualYield,
  frequency,
  maturityDate,
  minInvestment = 0,
  maxInvestment
}: YieldCalculatorProps) {
  const [investmentAmount, setInvestmentAmount] = useState(10000 * 100); // R$ 10.000 em centavos
  const [holdingPeriod, setHoldingPeriod] = useState(12); // meses

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(value / 100);
  };

  const calculations = useMemo(() => {
    const tokens = Math.floor(investmentAmount / tokenPrice);
    const actualInvestment = tokens * tokenPrice;
    
    // Yield por período
    const periodsPerYear = frequencyMultipliers[frequency];
    const yieldPerPeriod = annualYield / periodsPerYear;
    
    // Total de períodos no holding period
    const totalPeriods = Math.floor((holdingPeriod / 12) * periodsPerYear);
    
    // Rendimento total (juros simples para simplificar)
    const totalYield = actualInvestment * (annualYield / 100) * (holdingPeriod / 12);
    
    // Rendimento por período
    const yieldPerPayment = actualInvestment * (yieldPerPeriod / 100);
    
    // Valor final
    const finalValue = actualInvestment + totalYield;
    
    return {
      tokens,
      actualInvestment,
      yieldPerPeriod,
      totalPeriods,
      totalYield,
      yieldPerPayment,
      finalValue
    };
  }, [investmentAmount, tokenPrice, annualYield, frequency, holdingPeriod]);

  return (
    <Card className="p-6">
      <div className="flex items-center gap-2 mb-6">
        <Calculator className="w-5 h-5 text-primary-400" />
        <h3 className="font-semibold text-surface-100">Calculadora de Rendimento</h3>
      </div>

      <div className="space-y-6">
        {/* Input: Valor do Investimento */}
        <div>
          <label className="block text-sm text-surface-400 mb-2">Valor do Investimento</label>
          <div className="relative">
            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-surface-400">R$</span>
            <input
              type="number"
              value={investmentAmount / 100}
              onChange={(e) => setInvestmentAmount(Number(e.target.value) * 100)}
              min={minInvestment / 100}
              max={maxInvestment ? maxInvestment / 100 : undefined}
              className="w-full bg-surface-800 border border-surface-700 rounded-lg pl-10 pr-4 py-2 text-surface-100 focus:border-primary-500 focus:outline-none"
            />
          </div>
          {minInvestment > 0 && (
            <p className="text-xs text-surface-500 mt-1">
              Mínimo: {formatCurrency(minInvestment)}
              {maxInvestment && ` • Máximo: ${formatCurrency(maxInvestment)}`}
            </p>
          )}
        </div>

        {/* Input: Período */}
        <div>
          <label className="block text-sm text-surface-400 mb-2">Período de Investimento</label>
          <div className="grid grid-cols-4 gap-2">
            {[6, 12, 24, 36].map((months) => (
              <button
                key={months}
                onClick={() => setHoldingPeriod(months)}
                className={`py-2 rounded-lg text-sm font-medium transition-colors ${
                  holdingPeriod === months
                    ? 'bg-primary-500/20 text-primary-400 border border-primary-500/50'
                    : 'bg-surface-800 text-surface-400 border border-surface-700 hover:border-surface-600'
                }`}
              >
                {months} meses
              </button>
            ))}
          </div>
        </div>

        {/* Info Cards */}
        <div className="grid grid-cols-2 gap-4">
          <div className="p-3 bg-surface-800/50 rounded-lg">
            <div className="flex items-center gap-2 text-surface-400 text-xs mb-1">
              <Coins className="w-3 h-3" />
              Tokens
            </div>
            <p className="text-lg font-semibold text-surface-100">
              {calculations.tokens.toLocaleString('pt-BR')}
            </p>
          </div>
          <div className="p-3 bg-surface-800/50 rounded-lg">
            <div className="flex items-center gap-2 text-surface-400 text-xs mb-1">
              <TrendingUp className="w-3 h-3" />
              Rendimento a.a.
            </div>
            <p className="text-lg font-semibold text-green-400">
              {annualYield.toFixed(2)}%
            </p>
          </div>
        </div>

        {/* Results */}
        <div className="border-t border-surface-700 pt-4 space-y-3">
          <div className="flex justify-between items-center">
            <span className="text-surface-400">Investimento Efetivo</span>
            <span className="text-surface-100 font-medium">
              {formatCurrency(calculations.actualInvestment)}
            </span>
          </div>
          <div className="flex justify-between items-center">
            <span className="text-surface-400">Frequência de Pagamento</span>
            <span className="text-surface-100">{frequencyLabels[frequency]}</span>
          </div>
          <div className="flex justify-between items-center">
            <span className="text-surface-400">Rendimento por Pagamento</span>
            <span className="text-green-400 font-medium">
              {formatCurrency(calculations.yieldPerPayment)}
            </span>
          </div>
          <div className="flex justify-between items-center">
            <span className="text-surface-400">Total de Pagamentos</span>
            <span className="text-surface-100">{calculations.totalPeriods}</span>
          </div>
          <div className="flex justify-between items-center pt-3 border-t border-surface-700">
            <span className="text-surface-400">Rendimento Total Estimado</span>
            <span className="text-green-400 font-semibold text-lg">
              {formatCurrency(calculations.totalYield)}
            </span>
          </div>
          <div className="flex justify-between items-center">
            <span className="text-surface-400">Valor Final Estimado</span>
            <span className="text-primary-400 font-bold text-xl">
              {formatCurrency(calculations.finalValue)}
            </span>
          </div>
        </div>

        {maturityDate && (
          <div className="flex items-center gap-2 p-3 bg-yellow-500/10 rounded-lg">
            <Calendar className="w-4 h-4 text-yellow-400" />
            <span className="text-sm text-yellow-400">
              Vencimento: {new Date(maturityDate).toLocaleDateString('pt-BR')}
            </span>
          </div>
        )}

        <p className="text-xs text-surface-500 text-center">
          * Valores estimados. Rendimentos passados não garantem rendimentos futuros.
        </p>
      </div>
    </Card>
  );
}
